<template>
  <div class="session-picker">
    <div class="text-subtitle-1 font-weight-bold mb-1">
      Selecione uma data
    </div>
    <div class="text-caption text-medium-emphasis mb-3">
      Horários referentes ao local do evento.
    </div>

    <v-card elevation="0" class="session-picker__card pa-2">
      <div class="session-picker__scroll">
        <button
          v-for="group in groupedDays"
          :key="group.dayKey"
          class="session-picker__date-btn"
          :class="{ 'session-picker__date-btn--active': selectedDayKey === group.dayKey }"
          @click="onDayClick(group)"
        >
          <span class="session-picker__weekday">{{ group.weekday }}</span>
          <span class="session-picker__date">{{ group.dayLabel }}</span>
          <span class="session-picker__time">{{ group.sessions.length === 1 ? group.sessions[0].time : "+" + group.sessions.length + " horários" }}</span>
          <span v-if="selectedDayKey === group.dayKey" class="session-picker__arrow" />
        </button>
      </div>
    </v-card>

    <!-- Time slot picker: shown when selected day has multiple slots -->
    <div v-if="selectedGroup && selectedGroup.sessions.length > 1" class="mt-3">
      <div class="text-subtitle-1 font-weight-bold mb-2">
        Selecione um horário
      </div>

      <!-- Pills for ≤10 slots -->
      <v-card class="d-flex flex-wrap gap-2 justify-center py-4" style="gap: 8px;" elevation="0">
        <button
          v-for="slot in selectedGroup.sessions"
          :key="slot.id"
          class="session-picker__time-btn"
          :class="{ 'session-picker__time-btn--active': selectedSlot?.id === slot.id }"
          @click="onSlotSelect(slot.id)"
        >
          {{ slot.time }}
        </button>
      </v-card>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, Ref, ref } from "vue";
import { DateTime } from "luxon";
import { Session } from "@/types/Session";

// TODO: Obviously this is just mock data, we need to receive the sessions as a prop and emit the selected session back to the parent component. This is just to facilitate the development of the UI while we don't have the API ready.
const sessions: Ref<Session[]> = ref([
  {
    id: 1,
    date: "2026-03-28T09:00:00",
  },
  {
    id: 2,
    date: "2026-03-28T10:00:00",
  },
  {
    id: 3,
    date: "2026-03-28T11:00:00",
  },
  {
    id: 4,
    date: "2026-03-28T12:00:00",
  },
  {
    id: 5,
    date: "2026-03-28T13:00:00",
  },
  {
    id: 6,
    date: "2026-03-28T14:00:00",
  },
  {
    id: 7,
    date: "2026-03-28T15:00:00",
  },
  {
    id: 8,
    date: "2026-03-28T16:00:00",
  },
  {
    id: 9,
    date: "2026-03-28T17:00:00",
  },
  {
    id: 10,
    date: "2026-03-28T18:00:00",
  },
  {
    id: 11,
    date: "2026-03-29T09:00:00",
  },
  {
    id: 12,
    date: "2026-03-29T10:00:00",
  },
  {
    id: 13,
    date: "2026-03-29T11:00:00",
  },
  {
    id: 14,
    date: "2026-03-29T12:00:00",
  },
  {
    id: 15,
    date: "2026-03-29T13:00:00",
  },
  {
    id: 16,
    date: "2026-03-29T14:00:00",
  },
  {
    id: 17,
    date: "2026-03-29T15:00:00",
  },
  {
    id: 18,
    date: "2026-03-29T16:00:00",
  },
  {
    id: 19,
    date: "2026-03-29T17:00:00",
  },
  {
    id: 20,
    date: "2026-03-29T18:00:00",
  },
]);

const emit = defineEmits<{
  submit: [session: Session];
}>();

interface SlotItem {
  id: string | number;
  time: string;
  session: Session;
}

interface DayGroup {
  dayKey: string;
  weekday: string;
  dayLabel: string;
  sessions: SlotItem[];
}

const selectedDayKey = ref<string | null>(null);
const selectedSlot = ref<SlotItem | null>(null);

onMounted(() => {
  const firstGroup = groupedDays.value[0];
  if (!firstGroup) return;
  onDayClick(firstGroup);
  onSlotSelect(firstGroup.sessions[0].id);
});

const groupedDays = computed<DayGroup[]>(() => {
  const map = new Map<string, DayGroup>();

  for (const session of sessions.value) {
    const dt = DateTime.fromISO(session.date);
    const dayKey = dt.toFormat("yyyy-MM-dd");

    if (!map.has(dayKey)) {
      map.set(dayKey, {
        dayKey,
        weekday: dt.toFormat("cccc"),
        dayLabel: dt.toFormat("dd LLL"),
        sessions: [],
      });
    }

    map.get(dayKey)!.sessions.push({
      id: session.id,
      time: dt.toFormat("HH:mm"),
      session,
    });
  }

  return Array.from(map.values());
});

const selectedGroup = computed<DayGroup | null>(() => {
  if (!selectedDayKey.value) return null;
  return groupedDays.value.find(g => g.dayKey === selectedDayKey.value) ?? null;
});

function onDayClick(group: DayGroup): void {
  if (selectedDayKey.value === group.dayKey) return;

  selectedDayKey.value = group.dayKey;
  selectedSlot.value = null;

  if (group.sessions.length === 1) {
    selectedSlot.value = group.sessions[0];
    emit("submit", group.sessions[0].session);
  }
}

function onSlotSelect(id: string | number): void {
  const slot = selectedGroup.value?.sessions.find(s => s.id === id) ?? null;
  if (!slot) return;
  selectedSlot.value = slot;
  emit("submit", slot.session);
}
</script>

<style scoped>
.session-picker__card {
  overflow: hidden;
}

.session-picker__scroll {
  display: flex;
  gap: 4px;
  overflow-x: auto;
  scrollbar-width: none;
  padding-bottom: 4px;
}

.session-picker__scroll::-webkit-scrollbar {
  display: none;
}

.session-picker__date-btn {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  padding: 8px 12px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
  line-height: 1.4;
  color: inherit;
}

.session-picker__date-btn:hover:not(.session-picker__date-btn--active) {
  background: rgba(0, 0, 0, 0.06);
}

.session-picker__date-btn--active {
  background: rgb(var(--v-theme-primary));
  color: #fff;
}

.session-picker__weekday {
  font-size: 0.7rem;
}

.session-picker__date {
  font-size: 1rem;
  font-weight: 700;
}

.session-picker__time {
  font-size: 0.75rem;
}

/* Downward triangle pointer on active card */
.session-picker__arrow {
  position: absolute;
  bottom: -7px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-top: 7px solid rgb(var(--v-theme-primary));
}

.session-picker__time-btn {
  padding: 6px 16px;
  border: 1.5px solid rgba(0, 0, 0, 0.2);
  border-radius: 20px;
  background: transparent;
  cursor: pointer;
  font-size: 0.875rem;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  color: inherit;
}

.session-picker__time-btn:hover:not(.session-picker__time-btn--active) {
  background: rgba(0, 0, 0, 0.06);
}

.session-picker__time-btn--active {
  background: rgb(var(--v-theme-primary));
  border-color: rgb(var(--v-theme-primary));
  color: #fff;
}
</style>
